import { Loader2, ThumbsUp, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

// Define the structure for the location state
interface LocationState {
  city: string;
  region: string;
  country: string; // Add country
  lat: number;     // Add latitude
  lng: number;     // Add longitude
  withinServiceArea: boolean;
  loading: boolean;
  error: string | null;
}

// Coordinates of the company headquarters from environment variables
const HEADQUARTERS_COORDS = {
  lat: parseFloat(import.meta.env.VITE_HQ_LAT || '-22.9064'),
  lng: parseFloat(import.meta.env.VITE_HQ_LNG || '-47.0616')
};

// Maximum service radius in kilometers
const SERVICE_RADIUS_KM = 150;

// API token from environment variables
const IPINFO_TOKEN = import.meta.env.VITE_IPINFO_TOKEN || '128bc38f03be1b';

// Enable test mode for development
const TEST_MODE = import.meta.env.VITE_TEST_MODE === 'true';
const TEST_LOCATION = {
  city: 'São Paulo',
  region: 'SP',
  distance: 95,
  withinServiceArea: true
};

export const VisitorLocation = () => {
  // Initialize state with all properties
  const [location, setLocation] = useState<LocationState>({
    city: 'sua cidade', // Default text
    region: '',
    country: '', // Initialize country
    lat: 0,      // Initialize lat
    lng: 0,      // Initialize lng
    withinServiceArea: false,
    loading: true,
    error: null
  });

  useEffect(() => {
    // Only use IP-based location detection
    fetchLocationByIP();
  }, []);

  // Function to handle IP-based location fetching
  function fetchLocationByIP() {
    // Check for cached location first
    const cachedLocation = localStorage.getItem('cachedLocation');
    const cacheTimestamp = localStorage.getItem('locationCacheTimestamp');
    const now = Date.now();
    
    // Use cache if available and less than 24 hours old
    if (cachedLocation && cacheTimestamp && (now - parseInt(cacheTimestamp)) < 86400000) {
      const { city, region, country, lat, lng, withinServiceArea } = JSON.parse(cachedLocation);
      setLocation({
        city,
        region,
        country,
        lat,
        lng,
        withinServiceArea,
        loading: false,
        error: null
      });
      return;
    }

    // Fetch new location data if no cache or cache expired
    fetch(`https://ipinfo.io/json?token=${IPINFO_TOKEN}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.loc) {
          const [lat, lng] = data.loc.split(',').map((coord: string) => parseFloat(coord));
          const distance = calculateDistance(
            lat,
            lng,
            HEADQUARTERS_COORDS.lat,
            HEADQUARTERS_COORDS.lng
          );
          const withinServiceArea = distance <= SERVICE_RADIUS_KM;

          const locationData = {
            city: data.city || 'Desconhecida',
            region: data.region || 'Desconhecida',
            country: data.country || 'Desconhecido', // Use fetched country
            lat: lat,                              // Use fetched lat
            lng: lng,                              // Use fetched lng
            withinServiceArea
          };
          
          // Cache the location data with timestamp
          localStorage.setItem('cachedLocation', JSON.stringify(locationData));
          localStorage.setItem('locationCacheTimestamp', now.toString());

          setLocation({
            ...locationData,
            loading: false,
            error: null
          });
        }
      })
      .catch(ipError => {
        console.error('Error fetching location data:', ipError);
        setLocation({
          city: 'Desconhecida', // Default error text
          region: 'Desconhecida',
          country: 'Desconhecido', // Default error text
          lat: 0,                // Default error value
          lng: 0,                // Default error value
          withinServiceArea: false,
          loading: false,
          error: 'Não foi possível determinar a localização automaticamente.'
        });
      });
  }

  if (location.loading) {
    return (
      <div className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 mt-2 p-2 bg-white/80 dark:bg-gray-800/80 rounded border border-gray-200 dark:border-gray-700 backdrop-blur-sm">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        <span>Detectando sua localização...</span>
      </div>
    );
  }

  return (
    <div className="text-xs mt-2 p-2 bg-white/80 dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-700 backdrop-blur-sm shadow-sm text-center">
      {location.withinServiceArea ? (
        <div className="flex items-center justify-center text-green-600 dark:text-green-400">
          <ThumbsUp className="w-4 h-4 mr-1" />
          <span dangerouslySetInnerHTML={{ __html: `Bem-vindo(a) a <strong>${location.city}${location.region ? `, ${location.region}` : ''}</strong>! 😊 Agende uma visita ou tire suas dúvidas.` }} />
        </div>
      ) : (
        <div className="flex items-center justify-center text-blue-600 dark:text-blue-400">
          <XCircle className="w-4 h-4 mr-1" />
          <span dangerouslySetInnerHTML={{ __html: `Fora da área de cobertura: <strong>${location.city}${location.region ? `, ${location.region}` : ''}</strong>. 😢 Mesmo assim, vamos ajudar!` }} />
        </div>
      )}
    </div>
  );
}

// Function to calculate distance between two points using the Haversine formula
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Radius of the Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in km
  return Math.round(distance);
} 